import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, Edit, Globe, Calendar, Phone, CreditCard, Languages, MapPin, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import useCountryStore from '@/stores/countryStore'
import { Currency } from '@/services/configService'
import configService from '@/services/configService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const CountryDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    currentCountry,
    loading,
    error,
    fetchCountryById,
    clearCurrentCountry,
    deleteCountry
  } = useCountryStore()

  const [currency, setCurrency] = useState<Currency | null>(null)
  const [loadingCurrency, setLoadingCurrency] = useState(false)

  useEffect(() => {
    if (id) {
      fetchCountryById(id)
    }

    return () => {
      clearCurrentCountry()
    }
  }, [id])

  // Charger la devise lorsque le pays est chargé
  useEffect(() => {
    const loadCurrency = async () => {
      if (currentCountry?.default_currency_id) {
        setLoadingCurrency(true)
        try {
          const currencyData = await configService.currencies.getById(currentCountry.default_currency_id)
          setCurrency(currencyData)
        } catch (error) {
          console.error('Error loading currency:', error)
        } finally {
          setLoadingCurrency(false)
        }
      }
    }

    if (currentCountry) {
      loadCurrency()
    }
  }, [currentCountry])

  const handleDelete = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pays ? Cette action est irréversible.')) {
      try {
        if (id) {
          await deleteCountry(id)
          navigate('/countries')
        }
      } catch (error) {
        console.error('Error deleting country:', error)
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getLanguageName = (languageCode: string | null | undefined) => {
    if (!languageCode) return 'Non spécifié'
    
    const languages: Record<string, string> = {
      'fr': 'Français',
      'en': 'English',
      'es': 'Español',
      'de': 'Deutsch',
      'it': 'Italiano',
      'pt': 'Português',
      'ru': 'Русский',
      'zh': '中文',
      'ja': '日本語',
      'ar': 'العربية'
    }
    return languages[languageCode] || languageCode
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Détails du pays"
          breadcrumbs={[
            { label: 'Pays', href: '/countries' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/countries"
        />
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    )
  }

  if (!currentCountry) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Détails du pays"
          breadcrumbs={[
            { label: 'Pays', href: '/countries' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/countries"
        />
        <div className="bg-yellow-50 text-yellow-600 px-4 py-3 rounded-md">
          Pays non trouvé
        </div>
      </div>
    )
  }

  const breadcrumbs = [
    { label: 'Pays', href: '/countries' },
    { label: currentCountry.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: () => navigate(`/countries/${id}/edit`),
      variant: 'default' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: 'destructive' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title={currentCountry.name}
        subtitle={`Informations détaillées sur ${currentCountry.name}`}
        breadcrumbs={breadcrumbs}
        backUrl="/countries"
        actions={actions}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations générales">
            <div className="flex items-start gap-4">
              <div className="text-4xl">
                {currentCountry.flag_emoji || '🏳️'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-2xl font-bold">{currentCountry.name}</h3>
                  <Badge variant={currentCountry.enabled ? "default" : "secondary"} className="inline-flex items-center">
                    {currentCountry.enabled ? (
                      <>
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Activé
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-1 h-3 w-3" />
                        Désactivé
                      </>
                    )}
                  </Badge>
                </div>
                
                {currentCountry.native_name && (
                  <p className="text-gray-600 mb-4">
                    <span className="font-medium">Nom natif :</span> {currentCountry.native_name}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-mono font-bold">
                        {currentCountry.code}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Code ISO</p>
                      <p className="font-medium">{currentCountry.code}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Globe className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Continent</p>
                      <p className="font-medium">{currentCountry.continent || 'Non spécifié'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Configuration régionale">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Devise par défaut</p>
                    {loadingCurrency ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-gray-500">Chargement...</span>
                      </div>
                    ) : currency ? (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {currency.symbol}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {currency.code}
                        </Badge>
                        <span className="text-sm text-gray-600">{currency.name}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-red-500">
                        Devise non trouvée (ID: {currentCountry.default_currency_id})
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Languages className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Langue par défaut</p>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {getLanguageName(currentCountry.default_language)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {currentCountry.default_language ? currentCountry.default_language.toUpperCase() : 'N/D'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fuseau horaire</p>
                    <p className="font-medium">{currentCountry.timezone}</p>
                  </div>
                </div>

                {currentCountry.phone_code && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Indicatif téléphonique</p>
                      <p className="font-medium">{currentCountry.phone_code}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </DetailSection>
        </div>

        {/* Informations secondaires et actions */}
        <div className="space-y-6">
          <DetailSection title="Paramètres fiscaux">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Calcul de la TVA</p>
                  <Badge variant={currentCountry.tax_calculation === 'inclusive' ? "default" : "secondary"} className="inline-flex items-center">
                    {currentCountry.tax_calculation === 'inclusive' ? 'TTC (TVA incluse)' : 'HT (TVA exclue)'}
                  </Badge>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Métadonnées">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Créé le</p>
                <p className="font-medium">{formatDate(currentCountry.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dernière modification</p>
                <p className="font-medium">{formatDate(currentCountry.updated_at)}</p>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate(`/countries/${id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Modifier le pays
              </Button>
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer le pays
              </Button>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default CountryDetails
